import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { cartAPI } from '../../services/storeClubAPI';
import ClubLayout from '../../components/StoreClub/ClubLayout';

// HelcimPay.js types are declared in src/types/helcim.d.ts

interface Product {
  id: number;
  name: string;
  brand: string;
  size_lbs: number;
  price: string;
  is_byoc: boolean;
}

interface CartItem {
  id: number;
  product: Product;
  quantity: number;
  line_total: string;
}

interface Cart {
  items: CartItem[];
  subtotal: string;
  card_processing_fee: string;
  total: string;
  item_count: number;
}

interface CheckoutData {
  order_id: number;
  checkout_token: string;
  amount: string;
  requires_payment: boolean;
}

const ClubCheckout: React.FC = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(true);
  const [pickupMethod, setPickupMethod] = useState<'locker' | 'in_store'>('locker');
  const [notes, setNotes] = useState('');
  const [step, setStep] = useState<'review' | 'payment'>('review');
  const [checkoutData, setCheckoutData] = useState<CheckoutData | null>(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState('');

  // Calculate totals based on pickup method
  const subtotal = cart ? parseFloat(cart.subtotal) : 0;
  const cardFee = pickupMethod === 'locker' ? subtotal * 0.03 : 0;
  const total = subtotal + cardFee;

  useEffect(() => {
    loadCart();
    // HelcimPay.js script loaded globally in index.html
  }, []);

  const loadCart = async () => {
    try {
      const data = await cartAPI.get();
      setCart(data);
      if (!data.items || data.items.length === 0) {
        navigate('/club/cart');
      }
    } catch (error) {
      console.error('Failed to load cart:', error);
      navigate('/club/cart');
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToPayment = async () => {
    if (pickupMethod === 'in_store') {
      // In-store pickup doesn't require payment - just create order
      await handleCreateOrder();
      return;
    }

    // For locker pickup, create order and get checkout token
    setProcessingPayment(true);
    setPaymentError('');

    try {
      const response = await cartAPI.initiateCheckout({
        pickup_method: pickupMethod,
        notes: notes || undefined,
      });

      if (response.requires_payment && response.checkout_token) {
        setCheckoutData(response);
        setStep('payment');

        // Initialize Helcim payment iframe
        setTimeout(() => {
          initializeHelcimPayment(response.checkout_token);
        }, 100);
      } else {
        // No payment required, order complete
        navigate(`/club/orders/${response.order_id}?new=true`);
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      setPaymentError(error.response?.data?.error || 'Failed to initialize checkout');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleCreateOrder = async () => {
    setProcessingPayment(true);
    try {
      const order = await cartAPI.checkout({
        pickup_method: pickupMethod,
        notes: notes || undefined,
      });
      navigate(`/club/orders/${order.id}?new=true`);
    } catch (error: any) {
      console.error('Order creation error:', error);
      setPaymentError(error.response?.data?.error || 'Failed to create order');
    } finally {
      setProcessingPayment(false);
    }
  };

  const initializeHelcimPayment = (checkoutToken: string) => {
    // Set up callback for Helcim payment result
    window.helcimPayCallback = (event) => {
      if (event.response === 1) {
        // Payment successful
        handlePaymentSuccess(event.transactionId);
      } else {
        // Payment failed
        setPaymentError(event.responseMessage || 'Payment failed');
        if (window.removeHelcimPayIframe) {
          window.removeHelcimPayIframe();
        }
      }
    };

    // Append the Helcim iframe (only checkoutToken needed per Helcim docs)
    if (window.appendHelcimPayIframe) {
      window.appendHelcimPayIframe(checkoutToken);
    }
  };

  const handlePaymentSuccess = async (transactionId?: string) => {
    if (!checkoutData) return;

    try {
      // Confirm payment with backend
      await cartAPI.confirmPayment({
        order_id: checkoutData.order_id,
        transaction_id: transactionId,
      });

      // Navigate to order confirmation
      navigate(`/club/orders/${checkoutData.order_id}?new=true&paid=true`);
    } catch (error) {
      console.error('Failed to confirm payment:', error);
      // Even if confirmation fails, payment went through
      navigate(`/club/orders/${checkoutData.order_id}?new=true`);
    }
  };

  if (loading) {
    return (
      <ClubLayout>
        <div style={styles.loading}>Loading checkout...</div>
      </ClubLayout>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <ClubLayout>
        <div style={styles.emptyCart}>
          <h2>Your cart is empty</h2>
          <Link to="/club/shop" style={styles.shopButton}>Shop Now</Link>
        </div>
      </ClubLayout>
    );
  }

  return (
    <ClubLayout>
      <div style={styles.container}>
        <div style={styles.header}>
          <Link to="/club/cart" style={styles.backLink}>
            &larr; Back to Cart
          </Link>
          <h1 style={styles.title}>
            {step === 'review' ? 'Checkout' : 'Payment'}
          </h1>
        </div>

        {step === 'review' && (
          <>
            {/* Order Summary */}
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>Order Summary</h2>
              <div style={styles.orderItems}>
                {cart.items.map((item) => (
                  <div key={item.id} style={styles.orderItem}>
                    <span style={styles.itemName}>
                      {item.quantity}x {item.product.name}
                    </span>
                    <span style={styles.itemPrice}>${item.line_total}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Pickup Method */}
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>Pickup Method</h2>

              <label style={styles.pickupOption}>
                <input
                  type="radio"
                  name="pickup"
                  value="locker"
                  checked={pickupMethod === 'locker'}
                  onChange={() => setPickupMethod('locker')}
                  style={styles.radio}
                />
                <div style={styles.pickupContent}>
                  <div style={styles.pickupHeader}>
                    <span style={styles.pickupIcon}>🔐</span>
                    <span style={styles.pickupName}>24/7 Locker Pickup</span>
                    <span style={styles.pickupFee}>+3% card fee</span>
                  </div>
                  <p style={styles.pickupDescription}>
                    Pay now, pick up anytime with a one-time access code
                  </p>
                </div>
              </label>

              <label style={styles.pickupOption}>
                <input
                  type="radio"
                  name="pickup"
                  value="in_store"
                  checked={pickupMethod === 'in_store'}
                  onChange={() => setPickupMethod('in_store')}
                  style={styles.radio}
                />
                <div style={styles.pickupContent}>
                  <div style={styles.pickupHeader}>
                    <span style={styles.pickupIcon}>🏪</span>
                    <span style={styles.pickupName}>In-Store Pickup</span>
                    <span style={styles.pickupFee}>No fee</span>
                  </div>
                  <p style={styles.pickupDescription}>
                    Pick up during business hours, pay at counter
                  </p>
                </div>
              </label>
            </div>

            {/* Order Notes */}
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>Order Notes (Optional)</h2>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Special instructions..."
                style={styles.notesInput}
                rows={3}
              />
            </div>

            {/* Price Summary */}
            <div style={styles.priceSummary}>
              <div style={styles.priceRow}>
                <span>Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              {pickupMethod === 'locker' && (
                <div style={styles.priceRow}>
                  <span>Card Processing Fee (3%)</span>
                  <span>${cardFee.toFixed(2)}</span>
                </div>
              )}
              <div style={styles.totalRow}>
                <span>Total</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>

            {paymentError && (
              <div style={styles.errorMessage}>{paymentError}</div>
            )}

            {/* Action Button */}
            <button
              onClick={handleProceedToPayment}
              disabled={processingPayment}
              style={styles.checkoutButton}
            >
              {processingPayment ? (
                'Processing...'
              ) : pickupMethod === 'locker' ? (
                `Pay $${total.toFixed(2)} Now`
              ) : (
                'Place Order'
              )}
            </button>

            {pickupMethod === 'in_store' && (
              <p style={styles.noteText}>
                You'll pay when you pick up your order during business hours.
              </p>
            )}
          </>
        )}

        {step === 'payment' && (
          <div style={styles.paymentContainer}>
            <div style={styles.paymentHeader}>
              <h2 style={styles.paymentTitle}>Complete Payment</h2>
              <p style={styles.paymentAmount}>Amount: ${checkoutData?.amount}</p>
            </div>

            {paymentError && (
              <div style={styles.errorMessage}>
                {paymentError}
                <button
                  onClick={() => {
                    setPaymentError('');
                    setStep('review');
                  }}
                  style={styles.retryButton}
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Helcim payment iframe will be injected here */}
            <div id="helcim-pay-iframe-container" style={styles.helcimContainer} />

            <p style={styles.secureNote}>
              🔒 Payments are processed securely by Helcim
            </p>
          </div>
        )}
      </div>
    </ClubLayout>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '1rem',
    paddingBottom: '2rem',
    maxWidth: '600px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '1.5rem',
  },
  backLink: {
    color: '#508590',
    textDecoration: 'none',
    fontSize: '0.9rem',
    marginBottom: '0.5rem',
    display: 'inline-block',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#1f2937',
    margin: 0,
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '50vh',
    color: '#666',
  },
  emptyCart: {
    textAlign: 'center' as const,
    padding: '3rem 1rem',
  },
  shopButton: {
    display: 'inline-block',
    backgroundColor: '#508590',
    color: 'white',
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    textDecoration: 'none',
    fontWeight: 600,
  },
  section: {
    marginBottom: '1.5rem',
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '0.75rem',
  },
  orderItems: {
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    padding: '1rem',
  },
  orderItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
    borderBottom: '1px solid #e5e7eb',
  },
  itemName: {
    color: '#374151',
    fontSize: '0.9rem',
  },
  itemPrice: {
    color: '#1f2937',
    fontWeight: 500,
  },
  pickupOption: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    padding: '1rem',
    backgroundColor: 'white',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    marginBottom: '0.75rem',
    cursor: 'pointer',
  },
  radio: {
    marginTop: '4px',
    width: '20px',
    height: '20px',
    accentColor: '#508590',
  },
  pickupContent: {
    flex: 1,
  },
  pickupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flexWrap: 'wrap' as const,
  },
  pickupIcon: {
    fontSize: '1.25rem',
  },
  pickupName: {
    fontWeight: 600,
    color: '#1f2937',
  },
  pickupFee: {
    fontSize: '0.75rem',
    backgroundColor: '#f3f4f6',
    color: '#6b7280',
    padding: '0.125rem 0.5rem',
    borderRadius: '4px',
  },
  pickupDescription: {
    fontSize: '0.85rem',
    color: '#6b7280',
    margin: '0.5rem 0 0 0',
  },
  notesInput: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '1rem',
    fontFamily: 'inherit',
    resize: 'vertical' as const,
  },
  priceSummary: {
    backgroundColor: '#f9fafb',
    borderRadius: '12px',
    padding: '1rem',
    marginBottom: '1.5rem',
  },
  priceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
    fontSize: '0.9rem',
    color: '#4b5563',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.75rem 0 0 0',
    marginTop: '0.5rem',
    borderTop: '2px solid #e5e7eb',
    fontSize: '1.125rem',
    fontWeight: 700,
    color: '#1f2937',
  },
  errorMessage: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#dc2626',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
    fontSize: '0.9rem',
  },
  checkoutButton: {
    width: '100%',
    padding: '1rem',
    backgroundColor: '#508590',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '1.1rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  noteText: {
    textAlign: 'center' as const,
    fontSize: '0.85rem',
    color: '#6b7280',
    marginTop: '1rem',
  },
  paymentContainer: {
    marginTop: '1rem',
  },
  paymentHeader: {
    textAlign: 'center' as const,
    marginBottom: '1.5rem',
  },
  paymentTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#1f2937',
    margin: '0 0 0.5rem 0',
  },
  paymentAmount: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#508590',
    margin: 0,
  },
  helcimContainer: {
    minHeight: '400px',
    backgroundColor: '#f9fafb',
    borderRadius: '12px',
    padding: '1rem',
  },
  secureNote: {
    textAlign: 'center' as const,
    fontSize: '0.85rem',
    color: '#6b7280',
    marginTop: '1rem',
  },
  retryButton: {
    marginTop: '0.5rem',
    padding: '0.5rem 1rem',
    backgroundColor: '#dc2626',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
};

export default ClubCheckout;
